/**
 * OaSIS Explorer Frontend - Dataset Version Hook
 * Task 32.6: Frontend Version Routing
 * 
 * Extracts version from URL and provides version-aware utilities.
 * All versioned pages use this hook to get the current version for API calls.
 */

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { trpc } from '../api';
import { sanitizeMeasurementParamsForVersion } from '../utils/versionFieldMapping';

/**
 * Dataset version identifier.
 */
export type DatasetVersion = string;

/**
 * Version capabilities - features available in each version
 * Used for conditional rendering of version-specific features
 */
export interface VersionCapabilities {
  hasCoreCompetencies: boolean;
  hasSkillsMatch: boolean;
  hasLabels: boolean;
  hasMotorCoordination: boolean;
  hasHandEyeCoordination: boolean;
  hasGeneralLearningAbility: boolean;
  hasFormPerception: boolean;
}

export interface VersionSourceResourceSummary {
  resourceId: string;
  category: string;
  language: string;
  resourceLastModified: string | null;
  resourceMetadataModified: string | null;
  downloadMethod: string;
}

export interface VersionSourceSummary {
  sourceSnapshotCapturedAt: string | null;
  sourcePackageMetadataModified: string | null;
  resourceSummary: VersionSourceResourceSummary[];
}

/**
 * Safe fallback capabilities while metadata is loading.
 */
const DEFAULT_CAPABILITIES: VersionCapabilities = {
  hasCoreCompetencies: false,
  hasSkillsMatch: false,
  hasLabels: false,
  hasMotorCoordination: false,
  hasHandEyeCoordination: false,
  hasGeneralLearningAbility: false,
  hasFormPerception: false,
};

/**
 * Hook to get current dataset version from URL and provide version utilities
 * 
 * Usage:
 * ```tsx
 * const { version, capabilities, switchVersion } = useDatasetVersion();
 * 
 * // Use version in API calls
 * const { data } = trpc.occupation.search.useQuery({ version, query: '...' });
 * 
 * // Conditional rendering based on capabilities
 * {capabilities.hasCoreCompetencies && <CoreCompetenciesFilter />}
 * ```
 */
export function useDatasetVersion() {
  const { version: urlVersion } = useParams<{ version?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const nonVersionedDatasetOverride =
    urlVersion
      ? null
      : new URLSearchParams(location.search).get('dataset');

  const defaultVersionQuery = trpc.version.getDefault.useQuery(undefined, {
    staleTime: Infinity,
  });
  const versionsQuery = trpc.version.list.useQuery(undefined, {
    staleTime: Infinity,
  });
  const setPreferredVersionMutation = trpc.version.setPreferred.useMutation();

  const versions = versionsQuery.data?.versions ?? [];
  const defaultVersion =
    defaultVersionQuery.data?.version ??
    versions.find((v) => v.isDefault)?.version ??
    versions[0]?.version ??
    null;
  const isKnownUrlVersion = Boolean(urlVersion && versions.some((v) => v.version === urlVersion));
  const isKnownNonVersionedDatasetOverride = Boolean(
    nonVersionedDatasetOverride && versions.some((v) => v.version === nonVersionedDatasetOverride)
  );

  // Keep URL version while list query is loading to avoid early query churn.
  const currentVersion: DatasetVersion | null = isKnownUrlVersion
    ? (urlVersion as DatasetVersion)
    : (urlVersion && versions.length === 0
      ? urlVersion
          : isKnownNonVersionedDatasetOverride
        ? (nonVersionedDatasetOverride as DatasetVersion)
        : (nonVersionedDatasetOverride && versions.length === 0
          ? nonVersionedDatasetOverride
          : defaultVersion));

  // Fetch capabilities for current version
  const capabilitiesQuery = trpc.version.getCapabilities.useQuery(
    { version: currentVersion ?? '' },
    { 
      staleTime: Infinity, // Capabilities don't change during session
      enabled: Boolean(currentVersion),
    }
  );

  const currentVersionMetadata = versions.find((v) => v.version === currentVersion);
  const sourceSummary: VersionSourceSummary = {
    sourceSnapshotCapturedAt: currentVersionMetadata?.sourceSnapshotCapturedAt ?? null,
    sourcePackageMetadataModified: currentVersionMetadata?.sourcePackageMetadataModified ?? null,
    resourceSummary: (currentVersionMetadata?.resourceSummary as VersionSourceResourceSummary[] | undefined) ?? [],
  };

  // Switch to different version (navigates to new URL)
  const switchVersion = (newVersion: DatasetVersion) => {
    if (!newVersion || !currentVersion) return;
    if (newVersion === currentVersion) return;

    // Persist preference for authenticated users; anonymous requests are no-op server-side.
    setPreferredVersionMutation.mutate({ version: newVersion });
    
    const currentPath = location.pathname;
    
    // Check if we're on a versioned route
    const versionedRouteMatch = currentPath.match(/^\/([^/]+)\//);
    
    if (versionedRouteMatch) {
      // Replace version in URL
      const newPath = currentPath.replace(/^\/[^/]+\//, `/${newVersion}/`);
      const { params: sanitizedParams } = sanitizeMeasurementParamsForVersion(
        new URLSearchParams(location.search),
        newVersion
      );
      const nextSearch = sanitizedParams.toString();
      navigate(`${newPath}${nextSearch ? `?${nextSearch}` : ''}`);
    } else {
      // Stay on the current non-versioned page and sync via query param.
      const params = new URLSearchParams(location.search);
      params.set('dataset', newVersion);
      const { params: sanitizedParams } = sanitizeMeasurementParamsForVersion(params, newVersion);
      const nextSearch = sanitizedParams.toString();
      navigate(`${currentPath}${nextSearch ? `?${nextSearch}` : ''}`);
    }
  };
  
  // Check if current route is a versioned route
  const isVersionedRoute = Boolean(urlVersion);
  
  // Get the version from URL or return null if not on versioned route
  const urlVersionOrNull = isVersionedRoute ? currentVersion : null;
  
  return {
    /** Current dataset version resolved from URL/default metadata */
    version: currentVersion ?? '',
    /** Version from URL, or null if not on a versioned route */
    urlVersion: urlVersionOrNull,
    /** Whether current route is a versioned route */
    isVersionedRoute,
    /** Capabilities for current version */
    capabilities: (capabilitiesQuery.data?.capabilities as VersionCapabilities) ?? DEFAULT_CAPABILITIES,
    /** Switch to a different version (changes URL) */
    switchVersion,
    /** Source provenance summary for the selected dataset version */
    sourceSummary,
    /** Whether capabilities are still loading */
    isLoading:
      defaultVersionQuery.isLoading ||
      versionsQuery.isLoading ||
      (Boolean(currentVersion) && capabilitiesQuery.isLoading),
    /** Whether a concrete version has been resolved */
    hasResolvedVersion: Boolean(currentVersion),
  };
}

/**
 * Hook to get default version for non-versioned pages
 * Used by homepage and other pages that need to link to versioned routes
 */
export function useDefaultVersion() {
  const defaultQuery = trpc.version.getDefault.useQuery(undefined, {
    staleTime: Infinity,
  });
  const listQuery = trpc.version.list.useQuery(undefined, {
    staleTime: Infinity,
  });

  const defaultVersion =
    defaultQuery.data?.version ??
    listQuery.data?.versions.find((v) => v.isDefault)?.version ??
    listQuery.data?.versions[0]?.version ??
    null;

  return {
    defaultVersion: defaultVersion as DatasetVersion | null,
    isLoading: defaultQuery.isLoading || listQuery.isLoading,
  };
}

/**
 * Hook to get list of available versions
 * Used by VersionSelector component
 */
export function useAvailableVersions() {
  const { data, isLoading } = trpc.version.list.useQuery(undefined, {
    staleTime: Infinity,
  });
  
  return {
    versions: data?.versions ?? [],
    isLoading,
  };
}
